import React from "react";

interface HotelCardProps {
  image: string;
  name: string;
  location: string;
  rating: number;
  roomType: string;
  beds: string;
  amenities: string[];
  price: number;
  currency?: string;
}

const HotelCard: React.FC<HotelCardProps> = ({
  image,
  name,
  location,
  rating,
  roomType,
  beds,
  amenities,
  price,
  currency = "Rs.",
}) => {
  // Limit amenities to first 4, rest in scrollable container
  const visibleAmenities = amenities.slice(0, 4);
  const scrollAmenities = amenities.slice(4);

  return (
    <div className="flex bg-card rounded-xl shadow-md border border-card p-8 gap-8 items-stretch w-[900px] min-h-[340px] mx-auto">
      {/* Hotel Info Section */}
      <div className="flex flex-col justify-between flex-grow">
        <div>
          {/* Hotel Name & Rating */}
          <div className="flex justify-between items-start mb-2">
            <div>
              <h2 className="text-[28px] font-sora font-semibold text-black leading-tight mb-1">
                {name}
              </h2>
              <p className="text-base text-heading font-inter mb-2">
                {location}
              </p>
            </div>
            <div className="flex items-center gap-1  mr-2">
              <span className="text-[28px] font-sora font-semibold text-black">
                {rating}
              </span>
              <span className="text-primary text-[28px] font-bold">★</span>
            </div>
          </div>
          <hr className="border-t border-gray-300 mb-3" />

          {/* Room Type & Beds */}
          <div className="mb-2">
            <p className="text-subheading font-sora font-semibold text-black mb-1">
              {roomType}
            </p>
            <p className="text-sm text-heading font-inter mb-2">Beds: {beds}</p>
          </div>
          <hr className="border-t border-gray-300 mb-3" />

          {/* Amenities */}
          <div className="mb-2">
            <p className="text-subheading font-sora font-semibold text-black mb-1">
              Amenities
            </p>
            <div className="flex flex-wrap gap-1 text-sm text-heading font-inter">
              {visibleAmenities.map((amenity, index) => (
                <span key={index}>
                  {amenity}
                  {index < visibleAmenities.length - 1 ? "," : ""}
                </span>
              ))}
              {scrollAmenities.length > 0 && (
                <div
                  className="ml-2 border border-gray-300 rounded px-2 max-w-[180px] max-h-[24px] overflow-x-auto scrollbar-hide flex items-center"
                  style={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                >
                  {scrollAmenities.map((amenity, idx) => (
                    <span key={idx} className="whitespace-nowrap">
                      {amenity}
                      {idx < scrollAmenities.length - 1 ? "," : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Price & Payment Info */}
        <div className="mt-1 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-[28px] font-sora font-semibold text-black mb-1">
              {currency} {price}
            </div>
            <div className="text-sm text-heading font-inter">
              Inclusive all taxes, breakfast included
            </div>
          </div>
        </div>
      </div>

      {/* Hotel Image Section */}
      <div className="flex flex-col items-center flex-shrink-0 w-[320px]">
        <div className="w-[320px] h-[240px] overflow-hidden rounded-2xl mb-4">
          <img
            src={image || "/assets/hotel-placeholder.jpg"}
            alt={name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="text-sm text-heading mb-1">
          All payments methods accepted
        </div>
        <button className="w-full bg-primary text-white px-8 py-3 rounded-[12px] hover:bg-[#0a2580] transition text-[22px] font-sora font-semibold shadow">
          View Details
        </button>
      </div>
    </div>
  );
};

export default HotelCard;
